// The board: To-do, In Progress, In Review, Complete.
//
// WHAT IS ON IT IS REMINDERS - migration 0001's decision at the point
// `routine.status` is defined, not this screen's: "the board is a VIEW OF
// REMINDERS, not a second kind of thing". A reminder is a routine with no
// schedule (prototype-v2.md section 1.1) - it happens once and is finished for
// good - which is what a card on a board is. A repeating schedule item is not:
// it resets every day, so its Complete column would empty itself overnight and
// "In Review" would mean nothing about it. That work has a screen already, and
// it is the day.
//
// So this screen is also the first place a reminder can be MADE or SEEN in this
// client. The day's reminders block is not built and belongs to another
// workstream (web/src/README.md); a board with nothing on it and no way to put
// anything on it would have been the fifth instance of this client's oldest
// defect - an interface that succeeds while showing nothing - so every column
// that is not Complete carries its own composer.
//
// THE COLUMNS COME FROM THE SERVER, words and all. Nothing in this directory
// writes "In Review", knows which column completes, or knows that To-do is a
// null: the payload says, and a client sends the value back. That is the same
// rule that makes the day payload carry `schedules.cap` (AGENTS.md), and it is
// what lets a fifth column be a migration rather than an edit to two clients.
//
// IT IS A TAB (captain, 2026-08-18 - `shell/screens.ts`). It shipped as a pushed
// screen opened from a row in Settings, so it kept its own way out; a tab is
// never navigated away from by a Back control, and what a tab's top row carries
// instead is the account button - the way to Settings on a phone, hidden at
// sidebar width where the sidebar answers for it. Everything below the bar is
// the design the captain approved as shipped and is unchanged.

import { useState } from 'react';
import type { BoardResponse } from '../api/client.ts';
import { Icon } from '../day/Icon.tsx';
import { AccountButton } from '../shell/NavBar.tsx';
import { BoardColumnView } from './BoardColumnView.tsx';
import { archivedLine, boardLine } from './board-model.ts';
import type { BoardScreenState } from './useBoard.ts';

interface Props extends BoardScreenState {
  /** Opens Settings, which on a phone has no other door from here. */
  onSettings: () => void;
}

export function BoardScreen({
  board,
  loading,
  failure,
  busy,
  adding,
  loadingOlder,
  completePagingReady,
  loadOlder,
  move,
  add,
  archive,
  restore,
  retry,
  onSettings,
}: Props) {
  // Which card is under the pointer right now, for its own "being dragged" look.
  // The DROP does not read this - it reads the id back off the transfer - so a
  // second drag starting before the first is finished cannot move the wrong card.
  const [draggingId, setDraggingId] = useState<string | null>(null);

  return (
    <>
      {/* The bar the board shipped with, minus the Back control a tab has no
          use for. The account button takes its place on the right, which is
          where every other tab carries it, and an empty box of the same width
          balances it on the left so the centred title stays centred - both go
          at sidebar width, where the sidebar's own Settings entry answers for
          it and the title has the bar to itself (`styles.css`). */}
      <div className="topbar">
        <span className="avatarbalance" aria-hidden="true" />
        {/* The screen's own h1, at the top of its heading order, with each
            column's h2 under it and no gap between the two. */}
        <h1>Board</h1>
        <AccountButton onClick={onSettings} />
      </div>

      <div className="scroll">
        <div className="boardwrap">
          {board === null ? (
            // Whether the board is on its way is a state of its own, never inferred
            // from the absence of a reason it is not - the same three states the day
            // screen holds, for the same reason. `Try again` never appears for a
            // 401: that raises the signed-out screen instead, and re-sending a
            // sessionless request is the dead end this client already shipped once
            // (web/src/README.md).
            <div className="daystate" role="status">
              {loading ? (
                <p>Loading your board…</p>
              ) : (
                <>
                  <h3>Your board is not here.</h3>
                  <p>{failure}</p>
                  <button className="btn" onClick={retry}>
                    Try again
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {/* What to do, in the three states the board can be in. An empty
              board's line names a control that is on screen at every width -
              "Add a card", at the foot of every column below - rather than a +
              that is hidden at some of them, which is how the day screen's own
              empty state was wrong for an afternoon (`boardLine`). */}
              <p className="boardline">{boardLine(board)}</p>

              {/* Scrolls INSIDE ITSELF rather than pushing the page sideways: four
              columns do not fit on a phone, and nothing in this application may
              scroll the body horizontally (`styles.css`). */}
              <div className="board">
                {board.columns.map((column) => (
                  <BoardColumnView
                    key={column.key}
                    board={board}
                    column={column}
                    today={board.today}
                    busy={busy}
                    adding={adding.has(column.key)}
                    refreshing={loading}
                    loadingOlder={loadingOlder}
                    completePagingReady={completePagingReady}
                    draggingId={draggingId}
                    onLoadOlder={loadOlder}
                    onMove={move}
                    onArchive={archive}
                    onAdd={(title) => add(column.key, title)}
                    onDragStart={setDraggingId}
                    onDragEnd={() => setDraggingId(null)}
                  />
                ))}
              </div>

              {board.archived.length > 0 && (
                <ArchivedCards board={board} busy={busy} onRestore={restore} />
              )}

              {/* The failure of a RE-read, with a board still on screen. It says
              which of the two you are looking at rather than replacing a board
              that may still be right - the same shape the schedules list in
              Settings takes when its own read fails. */}
              {failure !== null && (
                <p className="sheetproblem" role="alert">
                  This board may be out of date. {failure}{' '}
                  <button className="btn" type="button" onClick={retry}>
                    Try again
                  </button>
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * The archived cards, and the way back from one.
 *
 * Archiving is undoable from the toast for ten seconds and then, without this,
 * from nowhere at all - which is exactly the hole an archived SCHEDULE had until
 * the Settings list closed it (web/src/README.md). A card is archived from the
 * board, so the door back has to be on the board.
 */
function ArchivedCards({
  board,
  busy,
  onRestore,
}: {
  board: BoardResponse;
  busy: ReadonlySet<string>;
  onRestore: (cardId: string) => void;
}) {
  return (
    <div className="barchive">
      <h2>Archived</h2>
      <ul className="setlist">
        {board.archived.map((card) => {
          return (
            <li className="setrow" key={card.id}>
              <span className="chip">
                <Icon name="archive" />
              </span>
              <span className="st">
                <b>{card.title}</b>
                <span>{archivedLine(card, board.today)}</span>
              </span>
              {/* Named for the card it puts back, for the reason Settings names
                  its own: a column of buttons all reading "Restore" says which
                  button exists and not which card it acts on. */}
              <button
                className="btn"
                aria-label={`${busy.has(card.id) ? 'Restoring' : 'Restore'} "${card.title}"`}
                disabled={busy.has(card.id)}
                onClick={() => onRestore(card.id)}
              >
                {busy.has(card.id) ? 'Restoring…' : 'Restore'}
              </button>
            </li>
          );
        })}
      </ul>
      <p className="finetext">
        Archiving takes a card off the board and keeps everything you have already finished in
        Progress. Restoring puts it back in the column it was in.
      </p>
    </div>
  );
}
